import React, {FC, useEffect, useReducer, useState} from 'react'
import InputField from '../../components/InputField'
import CkeditorField from '../../../common/CkeditorField'
import {fieldReducer, IProductProps, PRODUCT_NAME_MAX_LENGTH, productActionType, productInitialState} from '../../cors/reducers'
import CheckboxField from './CheckboxField'
import {useIntl} from 'react-intl'
import BarcodeField from './BarcodeField'
import {saveProductDraft} from '../../cors/_requests'
import SharedData from '../../../common/SharedData'
import BarcodeCompany from './BarcodeCompany'
import ProductMoreImages from './ProductMoreImages'
import ProductDataTab from '../ProductDataTab'
import ProductDiscountTab from '../ProductDiscountTab'
import VariationsField from './VariationsField'
import {useNavigate, useParams} from 'react-router-dom'
import CategoryStep from './CategoryStep'
import AlertMessage from '../../../common/alert/AlertMsg'
import {tenantTaxEnabled} from '../../../../tenant/tenantState'
import {isShopUser} from '../../../common/stockScope'

const STEP_COUNT = 5

const ProductGeneralForm: FC<IProductProps> = ({
  prodState,
  prodDispatch,
  currentStep,
  setCurrentStep,
  key2,
  isSubmitDisabled,
}) => {
  const {formatMessage} = useIntl()
  const [formStep, setFormStep] = useState(0) // 0–3 within currentStep === 2
  const [isChecked, setIsChecked] = useState(false)
  const {productID} = useParams()
  const {currentUser, language} = SharedData()
  const navigate = useNavigate()
  const [state, dispatch] = useReducer(fieldReducer, productInitialState)
  const [errorMsg, setErrorMsg] = useState({title: '', message: ''})

  const step = currentStep ?? 0

  useEffect(() => {
    if (step === 2 && !productID) {
      setFormStep(0)
    }
  }, [step])

  useEffect(() => {
    if (key2 === 'variations') setFormStep(0)
  }, [key2])

  // ── Breadcrumb ────────────────────────────────────────────────────────────
  // 1=Category, 2=Product Info, 3=Description, 4=Images, 5=Product Details
  const activeBreadcrumbStep = step === 1 ? 1 : Math.min(formStep + 2, 5)

  const breadcrumbLabel = (idx: number) => {
    switch (idx) {
      case 1: return formatMessage({id: 'category_selection'}) || 'Category Selection'
      case 2: return formatMessage({id: 'product_info_step'}) || 'Product Info'
      case 3: return formatMessage({id: 'desc'}) || 'Description'
      case 4: return formatMessage({id: 'images'}) || 'Images'
      case 5: return formatMessage({id: 'product_details_step'}) || 'Product Details'
      default: return ''
    }
  }

  const stepClass = (idx: number) => {
    if (idx < activeBreadcrumbStep) return 'aps-step-completed'
    if (idx === activeBreadcrumbStep) return 'aps-step-active'
    return 'aps-step-inactive'
  }

  // ── Validation ────────────────────────────────────────────────────────────
  const validateVariations = (): boolean => {
    let err = false
    prodState.variations.forEach((v) => {
      if (!v.barcode || !v.color_id || !v.size_id) err = true
      if (!v.quantity || v.quantity <= '0') err = true
      if (!v.price || v.price <= '0') err = true
    })
    if (prodState.variationImages.length !== prodState.variations.length) err = true
    return err
  }

  const validateTabErrors = (key: string): boolean => {
    switch (key) {
      case 'about':
        return !prodState.productNameEn.value || !prodState.productNameAr.value ||
          prodState.productNameEn.error || prodState.productNameAr.error
      case 'variations':
        return validateVariations()
      case 'productdescription':
        return prodState.productDescriptionEn.error || prodState.productDescriptionAr.error
      case 'image':
        if (typeof prodState.image.value === 'string' && prodState.image.value.includes('logos/img-placeholder.png')) return true
        return prodState.image.error
      case 'datatab':
        return !prodState.manufacturer_id.value ||
          (tenantTaxEnabled() && !prodState.tax_class_id.value) ||
          (!prodState.quantity.value && prodState.variations.length === 0) ||
          (!prodState.salePrice.value && prodState.variations.length === 0)
      default:
        return false
    }
  }

  const isFormStepAdvanceable = (s: number): boolean => {
    switch (s) {
      case 0: return !validateTabErrors('about') && !(isChecked && validateVariations())
      case 1: return !validateTabErrors('productdescription')
      case 2: return prodState.variations.length > 0 ? true : !validateTabErrors('image')
      default: return true
    }
  }

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {id, value} = e.target
    prodDispatch({type: productActionType.text, payload: {id, value}})
  }

  const handleChangeArabic = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {id, value} = e.target
    prodDispatch({type: productActionType.text, payload: {id, value: value.replace(/[A-Za-z]/g, '')}})
  }

  const resetVariations = () => {
    prodState.variations = []
    prodDispatch({type: productActionType.updateVariations, payload: prodState.variations})
  }

  const handleCheckboxChange = (checked: boolean) => {
    setIsChecked(checked)
    if (!checked) resetVariations()
  }

  const handleFormNext = () => {
    if (formStep < 3) setFormStep(formStep + 1)
  }

  const handleFormPrev = () => {
    if (formStep > 0) setFormStep(formStep - 1)
    else if (setCurrentStep) setCurrentStep(1)
  }

  const showAlert = (title: string, message: string) => {
    setErrorMsg({title, message})
    dispatch({type: productActionType.hasError, payload: {value: true}})
  }

  const handleCloseAlert = () => {
    setErrorMsg({title: '', message: ''})
    prodDispatch({type: productActionType.hasError, payload: {value: false}})
  }

  const saveDraft = async () => {
    try {
      const data = await saveProductDraft(language, currentUser?.token, prodState, productID, isShopUser(currentUser))
      if (data && 'errors' in data) {
        showAlert(formatMessage({id: 'image_error_fields'}), data.errors)
      } else {
        localStorage.setItem('thankYouPageURL', window.location.href)
        navigate('/products/drafted-list')
      }
    } catch (e: unknown) {
      showAlert(
        formatMessage({id: 'image_error_fields'}),
        e instanceof Error ? e.message : 'An unknown error occurred.',
      )
    }
  }

  // ── Shared sub-step nav row ───────────────────────────────────────────────
  const StepNav: FC<{onPrev: () => void; onNext?: () => void; nextDisabled?: boolean}> = ({
    onPrev,
    onNext,
    nextDisabled,
  }) => (
    <div className='aps-step-nav-row'>
      <button type='button' className='aps-prev-btn' onClick={onPrev}>
        {formatMessage({id: 'previous_step'}) || '← Previous Step'}
      </button>
      {onNext && (
        <button type='button' className='aps-next-btn' onClick={onNext} disabled={nextDisabled}>
          {formatMessage({id: 'next'}) || 'Next'}
        </button>
      )}
    </div>
  )

  // ── Form wizard (step 2) sub-steps ────────────────────────────────────────
  const renderFormStep = () => {
    switch (formStep) {
      case 0:
        return (
          <>
            <h3 className='aps-step-card-title'>
              {formatMessage({id: 'product_info_step'}) || 'Product Information'}
            </h3>

            <BarcodeCompany prodState={prodState} prodDispatch={prodDispatch} />

            <div className='aps-field-row mt-20'>
              <div className='aps-field-half'>
                <InputField
                  hasError={prodState.productNameEn.error}
                  value={prodState.productNameEn.value}
                  errorMsg={prodState.productNameEn.msg}
                  isRequired={true}
                  onChange={handleChange}
                  label={formatMessage({id: 'prod_name_en'})}
                  hint={formatMessage({id: 'prod_en_hint'})}
                  id='productNameEn'
                  maxLength={PRODUCT_NAME_MAX_LENGTH}
                />
              </div>
              <div className='aps-field-half'>
                <InputField
                  hasError={prodState.productNameAr.error}
                  errorMsg={prodState.productNameAr.msg}
                  value={prodState.productNameAr.value}
                  hint={formatMessage({id: 'prod_ar_hint'})}
                  isRequired={true}
                  label={formatMessage({id: 'prod_name_ar'})}
                  id='productNameAr'
                  onChange={handleChangeArabic}
                  maxLength={PRODUCT_NAME_MAX_LENGTH}
                />
              </div>
            </div>

            <div className='aps-field-row mt-20'>
              <div className='aps-field-half'>
                <CkeditorField
                  id='aboutEn'
                  label={formatMessage({id: 'about_prod_en'})}
                  hint={formatMessage({id: 'about_prod_hint'})}
                  prodDispatch={prodDispatch}
                  prodState={prodState}
                  value={prodState.aboutEn.value}
                  hasError={prodState.aboutEn.error}
                  errorMsg={prodState.aboutEn.msg}
                />
              </div>
              <div className='aps-field-half'>
                <CkeditorField
                  id='aboutAr'
                  label={formatMessage({id: 'about_prod_ar'})}
                  hint={formatMessage({id: 'about_prod_hint'})}
                  prodDispatch={prodDispatch}
                  prodState={prodState}
                  value={prodState.aboutAr.value}
                  hasError={prodState.aboutAr.error}
                  errorMsg={prodState.aboutAr.msg}
                />
              </div>
            </div>

            {/* A package is a fixed bundle of the supplier's own products, so it
                cannot also carry size/color variations — hide the option for it. */}
            {(!productID || String(productID).includes('&')) && !prodState.createPackage && (
              <CheckboxField
                isChecked={isChecked || prodState.variations.length > 0}
                setIsChecked={handleCheckboxChange}
              />
            )}

            {(isChecked || prodState.variations.length > 0) && !prodState.createPackage && (
              <div className='mt-20'>
                <VariationsField prodState={prodState} prodDispatch={prodDispatch} />
              </div>
            )}

            <StepNav onPrev={handleFormPrev} onNext={handleFormNext} nextDisabled={!isFormStepAdvanceable(0)} />
          </>
        )

      case 1:
        return (
          <>
            <h3 className='aps-step-card-title'>
              {formatMessage({id: 'marketing_description'}) || 'Marketing Description'}
            </h3>

            <div className='aps-field-row mt-20'>
              <div className='aps-field-half'>
                <CkeditorField
                  id='productDescriptionEn'
                  label={formatMessage({id: 'desc_en'})}
                  prodDispatch={prodDispatch}
                  prodState={prodState}
                  value={prodState.productDescriptionEn.value}
                  hasError={prodState.productDescriptionEn.error}
                  errorMsg={prodState.productDescriptionEn.msg}
                />
              </div>
              <div className='aps-field-half'>
                <CkeditorField
                  id='productDescriptionAr'
                  label={formatMessage({id: 'desc_ar'})}
                  prodDispatch={prodDispatch}
                  prodState={prodState}
                  value={prodState.productDescriptionAr.value}
                  hasError={prodState.productDescriptionAr.error}
                  errorMsg={prodState.productDescriptionAr.msg}
                />
              </div>
            </div>

            <StepNav onPrev={handleFormPrev} onNext={handleFormNext} nextDisabled={!isFormStepAdvanceable(1)} />
          </>
        )

      case 2:
        return (
          <>
            <h3 className='aps-step-card-title'>
              {formatMessage({id: 'images'}) || 'Product Images'}
            </h3>

            {prodState.variations.length === 0 && (
              <ProductMoreImages prodState={prodState} prodDispatch={prodDispatch} />
            )}
            {prodState.variations.length > 0 && (
              <p className='aps-step-hint'>
                {formatMessage({id: 'images_per_variation'}) || 'Images are managed per variation.'}
              </p>
            )}

            <StepNav onPrev={handleFormPrev} onNext={handleFormNext} nextDisabled={!isFormStepAdvanceable(2)} />
          </>
        )

      case 3:
        return (
          <>
            <h3 className='aps-step-card-title'>
              {formatMessage({id: 'product_details_step'}) || 'Product Details'}
            </h3>

            <ProductDataTab prodState={prodState} prodDispatch={prodDispatch} isChecked={isChecked} />

            {prodState.product_specials.length > 0 && (
              <div className='mt-20'>
                <ProductDiscountTab prodState={prodState} prodDispatch={prodDispatch} />
              </div>
            )}

            <StepNav onPrev={handleFormPrev} />
          </>
        )
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Barcode card — always visible for new products; read-only once past step 0 */}
      {!productID && (
        <BarcodeField
          prodState={prodState}
          prodDispatch={prodDispatch}
          handleChange={handleChange}
        />
      )}

      {/* ── Wizard (step 1+): breadcrumb THEN content ── */}
      {step >= 1 && (
        <div className='aps-wizard'>
          {/* Breadcrumb */}
          <div className='aps-step-breadcrumb'>
            {[1, 2, 3, 4, 5].map((idx) => (
              <span key={idx} className={`aps-step-item ${stepClass(idx)}`}>
                {breadcrumbLabel(idx)}
                {idx < STEP_COUNT && <span className='aps-step-chevron'>›</span>}
              </span>
            ))}
          </div>

          {/* Step 1: Category Selection */}
          {step === 1 && (
            <div className='aps-step-card'>
              <h3 className='aps-step-card-title'>
                {formatMessage({id: 'select_category'}) || 'Select Category'}
              </h3>
              <CategoryStep
                prodState={prodState}
                prodDispatch={prodDispatch}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
              />
              {/* Previous only — category selection auto-advances */}
              <div className='aps-step-nav-row'>
                <button
                  type='button'
                  className='aps-prev-btn'
                  onClick={() => setCurrentStep?.(0)}
                >
                  {formatMessage({id: 'previous_step'}) || '← Previous Step'}
                </button>
                <button
                  type='button'
                  className='aps-next-btn'
                  onClick={() => setCurrentStep?.(2)}
                  disabled={!prodState.categoryID.value}
                >
                  {formatMessage({id: 'next'}) || 'Next'}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Form wizard sub-steps */}
          {step === 2 && (
            <div className='aps-step-card'>
              {renderFormStep()}
            </div>
          )}
        </div>
      )}

      {/* Edit mode: start directly at the form (no barcode step, no category step) */}
      {productID && step === 2 && (
        <div className='aps-step-card'>
          {renderFormStep()}
        </div>
      )}

      {errorMsg.title && errorMsg.message && (
        <AlertMessage
          title={errorMsg.title}
          message={errorMsg.message}
          onClose={handleCloseAlert}
        />
      )}
    </>
  )
}

export default ProductGeneralForm
